import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Een regel bovenaan het gespreksscherm zodra er iets in het bakje wacht.
 *
 * **DIT IS GEEN POP-UP, EN DAT ONDERSCHEID IS DE EIS.** Eis 5 uit het bouwplan:
 * standaard uit, zichtbaar als een regel met een telling, en NOOIT een pop-up.
 * Deze regel onderbreekt niets, steelt geen aandacht van het invoerveld, vraagt
 * geen antwoord en verdwijnt op een klik. Wat hij wel doet is bestaan op het
 * scherm waar je toch al naar kijkt.
 *
 * **WAAROM DE ZIJBALKTELLING ALLEEN NIET GENOEG WAS.** Frank, 24 augustus 2026:
 * "die zie ik niet als ik niet naar Memory ga". De telling stond er wel, maar
 * als klein rondje naast een knop tussen andere knoppen. Dit is dezelfde
 * informatie op de plek waar zijn ogen zijn.
 *
 * **WEGGEKLIKT BLIJFT WEGGEKLIKT TOT ER IETS BIJ KOMT, en dat is met opzet.**
 * Zou hij bij elke paginalading terugkomen, dan is wegklikken geen antwoord maar
 * uitstel, en dan wordt hij binnen een week genegeerd zoals alles wat je niet
 * kunt wegkrijgen. Komt er een voorstel BIJ, dan is er nieuws en verschijnt hij
 * opnieuw: het weggeklikte getal wordt bewaard, niet een vlaggetje.
 */

const SLEUTEL = "novamund.wacht-in-geheugen.weggeklikt-bij";

export function magTonen(aantal, weggekliktBij) {
  if (!aantal) return false;
  const eerder = Number(weggekliktBij);
  if (!Number.isFinite(eerder)) return true;
  return aantal > eerder;
}

export default function WachtInGeheugen({ aantal = 0, onGaErheen, opslag }) {
  const { t } = useTaal();
  const bewaar = opslag || (typeof window !== "undefined" ? window.localStorage : null);
  const [weggeklikt, setWeggeklikt] = useState(null);

  useEffect(() => {
    try {
      setWeggeklikt(bewaar ? bewaar.getItem(SLEUTEL) : null);
    } catch {
      // Een browser die opslag weigert is geen reden om de regel niet te tonen.
      setWeggeklikt(null);
    }
  }, [bewaar]);

  if (!magTonen(aantal, weggeklikt)) return null;

  const klikWeg = () => {
    try {
      if (bewaar) bewaar.setItem(SLEUTEL, String(aantal));
    } catch {
      // Niet kunnen onthouden is vervelend, niet kunnen wegklikken is erger.
    }
    setWeggeklikt(String(aantal));
  };

  const zin = aantal === 1
    ? t("1 voorstel wacht in Geheugen")
    : t("{n} voorstellen wachten in Geheugen", { n: aantal });

  return (
    <div
      data-testid="wacht-in-geheugen"
      className={cn("flex items-center gap-2 border-b px-4 py-2 text-sm",
                    "text-muted-foreground")}
    >
      <Sparkles className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <button
        type="button"
        onClick={onGaErheen}
        data-testid="wacht-in-geheugen-erheen"
        className="truncate text-left underline-offset-2 hover:underline"
      >
        {zin}
      </button>
      <button
        type="button"
        onClick={klikWeg}
        aria-label={t("Melding wegklikken")}
        data-testid="wacht-in-geheugen-weg"
        className="ml-auto flex-shrink-0 rounded p-1 hover:bg-accent"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
